"use client";

import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import {
  Building2,
  LayoutDashboard,
  LogOut,
  Settings,
  Truck,
  Upload,
  Users,
} from "lucide-react";
import { BrandLogoMark } from "@/components/brand-logo";
import { useAuth } from "@/contexts/AuthContext";
import { APP_NAME, APP_TAGLINE } from "@/lib/brand";
import { userHasPermission, type InvoicePermissionKey } from "@/lib/invoice-permissions";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

const navItems: Array<{
  href: string;
  label: string;
  icon: typeof Upload;
  permission: InvoicePermissionKey;
}> = [
  { href: "/upload", label: "Upload", icon: Upload, permission: "upload" },
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard" },
  { href: "/stores", label: "Stores", icon: Building2, permission: "stores" },
  { href: "/suppliers", label: "Suppliers", icon: Truck, permission: "suppliers" },
  { href: "/users", label: "Users", icon: Users, permission: "users" },
  { href: "/settings", label: "Sync Settings", icon: Settings, permission: "settings_sync" },
];

export function AppSidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { signOut, user } = useAuth();
  const { toggleSidebar } = useSidebar();
  const visibleItems = navItems.filter((item) => user && userHasPermission(user, item.permission));

  const handleLogout = async () => {
    await signOut();
    router.push("/login");
  };

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={toggleSidebar}
              className="w-full cursor-pointer group-data-[collapsible=icon]:justify-center"
            >
              <BrandLogoMark size={24} />
              <div className="min-w-0 group-data-[collapsible=icon]:hidden">
                <span className="block font-semibold leading-tight">{APP_NAME}</span>
                <span className="block text-[10px] font-normal leading-tight text-muted-foreground">
                  {APP_TAGLINE}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map(({ href, label, icon: Icon }) => (
                <SidebarMenuItem key={href}>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === href || pathname.startsWith(href + "/")}
                  >
                    <Link href={href}>
                      <Icon className="size-4" />
                      <span>{label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => void handleLogout()}>
              <LogOut className="size-4" />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
